// Copyright 2024 - MinIO, Inc. All rights reserved.

import { ApiCalls, CAServerDrive } from "./types";

export const extractDomainsCommonParts = (servers: string[]): string[] => {
  // Split each domain into its components
  const splitDomains = servers.map((server) =>
    server.replace(":", ".").split("."),
  );

  // Find the common components across all domains
  return splitDomains.reduce((common, domain) => {
    return common.filter((component) => domain.includes(component));
  }, splitDomains[0]);
};

export const removeDomainCommonParts = (
  domain: string,
  commonComponents: string[],
): string => {
  // Extract and return the non-common parts
  const uniquePart = domain
    .replace(":", ".")
    .split(".")
    .filter((component) => !commonComponents.includes(component));
  return uniquePart.join(".");
};

export const cleanDriveEndpointCommon = (
  driveEndpoint: string,
  commonComponents: string[],
): string => {
  driveEndpoint = driveEndpoint.replace("https://", "").replace("http://", "");

  const slashIndex = driveEndpoint.indexOf("/");

  const host = driveEndpoint.substring(0, slashIndex);
  const path = driveEndpoint.substring(slashIndex + 1);

  let finalHost = removeDomainCommonParts(host, commonComponents);

  if (finalHost !== "" && path !== "") {
    return finalHost + "/" + path;
  }

  return "";
};

export const zoomSizes = {
  small: 18,
  medium: 30,
  large: 50,
};

export const convertUptime = (seconds: number): string => {
  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (days > 0) {
    return `${days} days`;
  }
  if (hours > 0) {
    return `${hours} hours`;
  }
  if (minutes > 0) {
    return `${minutes} minutes`;
  }

  return `${remainingSeconds} seconds`;
};

export const setConditions = [
  "normal",
  "warning",
  "severe",
  "critical",
  "unusable",
] as const;

export type SetCondition = (typeof setConditions)[number];

export const setCondition = (
  parity: number,
  setOffline: number,
): SetCondition => {
  if (setOffline > parity) {
    return "unusable";
  }
  if (setOffline === parity) {
    return "critical";
  }
  if (setOffline + 1 >= parity) {
    return "severe";
  }
  if (setOffline + 2 >= parity) {
    return "warning";
  }

  return "normal";
};

export const conditionClass = (cond: SetCondition): string => {
  switch (cond) {
    case "unusable":
      return "unusable";
    case "critical":
      return "critical";
    case "severe":
      return "severe";
    case "warning":
      return "warning";
    default:
      return "normal";
  }
};

export const driveProblems = [
  "nominal",
  "70-perc-usage",
  "80-perc-usage",
  "90-perc-usage",
  "70-perc-inodes",
  "80-perc-inodes",
  "90-perc-inodes",
  "offline",
  "healing",
  "io-errors",
  "timeouts",
] as const;

export type DriveProblem = (typeof driveProblems)[number];

export const driveProblemDescription = (dp: DriveProblem): string => {
  switch (dp) {
    case "nominal":
      return "nominal";
    case "70-perc-usage":
      return "Drive has used more than 70% of it's capacity";
    case "80-perc-usage":
      return "Drive has used more than 80% of it's capacity";
    case "90-perc-usage":
      return "Drive is running out of space (>90% usage)";
    case "70-perc-inodes":
      return "Drive has used more than 70% of it's Inodes";
    case "80-perc-inodes":
      return "Drive has used more than 80% of it's Inodes";
    case "90-perc-inodes":
      return "Drive is running out of Inodes (>90% usage)";
    case "offline":
      return "Drive is Offline";
    case "healing":
      return "Drive is healing";
    case "io-errors":
      return "Drive is reporting IO Errors and timeouts";
    case "timeouts":
      return "Drive is reporting  timeouts";
    default:
      return "";
  }
};

export interface Problem {
  message: string;
  severity: SetCondition;
}

export const detectProblems = (
  parity: number,
  drivesOffline: number,
  drivesHealing: number,
  drivesCritical: number,
): Problem[] => {
  let problems: Problem[] = [];
  if (drivesOffline > parity) {
    problems.push({
      message: "Erasure Set is unusable due to too many missing drives",
      severity: "unusable",
    });
  } else if (drivesOffline === parity) {
    problems.push({
      message: "Erasure Set is in read only mode due to lost quorum",
      severity: "critical",
    });
  } else if (drivesOffline + 1 >= parity) {
    problems.push({
      message: "Erasure Set is about to lose quorum",
      severity: "severe",
    });
  } else if (drivesOffline + 2 >= parity) {
    problems.push({
      message: "Erasure Set is two failures from losing quorum",
      severity: "warning",
    });
  }
  if (drivesHealing > 0) {
    problems.push({
      message: `${drivesHealing} drives are healing which may reduce the throughput of this erasure set`,
      severity: "normal",
    });
  }
  if (drivesCritical > 0) {
    problems.push({
      message: `${drivesCritical} drives are reporting timeouts and/or I/O Errors`,
      severity: "warning",
    });
  }
  return problems;
};

export const EmptyApiCalls = (): ApiCalls => {
  return {
    AppendFile: 0,
    CheckParts: 0,
    CreateFile: 0,
    Delete: 0,
    DeleteAbandonedParts: 0,
    DeleteVersion: 0,
    DeleteVersions: 0,
    DeleteVol: 0,
    DiskInfo: 0,
    ListDir: 0,
    ListVols: 0,
    MakeVol: 0,
    MakeVolBulk: 0,
    ReadAll: 0,
    ReadFile: 0,
    ReadFileStream: 0,
    ReadMultiple: 0,
    ReadVersion: 0,
    ReadXL: 0,
    RenameData: 0,
    RenameFile: 0,
    StatInfoFile: 0,
    StatVol: 0,
    UpdateMetadata: 0,
    VerifyFile: 0,
    WalkDir: 0,
    WriteAll: 0,
    WriteMetadata: 0,
  };
};

export const EmptyCaDrive = (withOverrides: {
  [key: string]: any;
}): CAServerDrive => {
  return {
    endpoint: "",
    path: "",
    state: "",
    uuid: "",
    major: 0,
    minor: 0,
    totalspace: 0,
    usedspace: 0,
    availspace: 0,
    pool_index: 0,
    set_index: 0,
    disk_index: 0,
    inode_free: 0,
    healing: false,
    metrics: {
      apiCalls: EmptyApiCalls(),
      lastMinute: {
        ListVols: {},
        ReadXL: {},
        WalkDir: {},
      },
      totalErrorsAvailability: 0,
      totalErrorsTimeout: 0,
    },
    drivePath: "",
    fs_type: "",
    free_inodes: 0,
    used_inodes: 0,
    ...withOverrides,
  };
};
